'use client'
import { type FC, memo, useRef } from 'react'
import { useParams } from 'next/navigation'
import styled from 'styled-components'
import { createUnderline, designGrid, font10, font12, font20, mq, vw, getVariants, font15} from '@/styles'
import { useModal, useTranslator } from '@/hooks'
import { MODALS } from '@/context'
import { motion, useInView } from 'motion/react'
import { getMultipleOfThree } from '@/utils'
import { TypeCmsRoom } from '@/schema'

type Props = {
  data?: Pick<TypeCmsRoom, 'pdpTitle' | 'pdpDescription' | 'pdpFeatures' | 'selfbookId'>
}



export const getIdBySelfbookId = (selfbookId: TypeCmsRoom['selfbookId']) => {
  switch (selfbookId) {
    case 'S':
      return 'SUI'
    case 'JS':
      return 'JSU'
    case 'DBL':
      return 'STD'
    default:
      return selfbookId
  }
}

export const SectionRoomInfo:FC<Props> = memo(({ data }) => {
  const { pdpTitle, pdpDescription, pdpFeatures, selfbookId } = data
  const { t } = useTranslator()
  const { lang } = useParams()
  const { toggle } = useModal(MODALS.BENEFITS)

  const ref = useRef<HTMLElement>(null)
	const isInView = useInView(ref, { margin: '0px', once: true })

  return (
    <>
      <Section ref={ref} variants={getVariants(isInView)} initial='initial' animate='animate' exit='exit'>
        <div>
          <span>{pdpTitle}</span>
          <div className='text-description' dangerouslySetInnerHTML={{ __html: pdpDescription }} />
          <a href={selfbookId ? `https://reservations.portellapalma.com/${lang}/hotel/portellapalma/room?roomId=${getIdBySelfbookId(selfbookId)}` : `https://reservations.portellapalma.com/${lang}/hotel/portellapalma`} rel='noreferrer noopener' target='_blank'>{t('project.footer.see_availability')}</a>
        </div>
        <div>
          <div className='services' dangerouslySetInnerHTML={{ __html: getMultipleOfThree(pdpFeatures) }}></div>
          <button className='only-desktop' onClick={toggle}>{t('project.prefooter.title')}</button>
        </div>
      </Section>
    </>
  )
})

const Section = styled(motion.section)`
  ${designGrid({})}

  > div {
    &:first-of-type {
      display: flex;
      flex-direction: column;
      grid-column: 1 / span 6;
      grid-row: 1 / span 1;
      padding: 0 ${vw(10, 'mobile')};

      ${mq.greaterThan('tablet')} {
        grid-column: 1 / span 6;
        grid-row: 1 / span 1;
        padding: 0 ${vw(50, 'desktop')};
      }

      > span {
        ${font20(true)}
        margin-bottom: ${vw(15, 'mobile')};
        text-align: center;
        text-transform: uppercase;

        ${mq.greaterThan('tablet')} {
          ${font20(false)}
          margin-bottom: ${vw(15, 'desktop')};
          text-align: justify;
        }
      }

      .text-description {
        margin-bottom: ${vw(40, 'mobile')};

        ${mq.greaterThan('tablet')} {
          margin-bottom: ${vw(40, 'desktop')};
        }

        p,
        h1,
        h2,
        h3,
        h4,
        h5,
        h6,
        span {
          ${font15(true)}
          text-align: center;
          letter-spacing: -.2px;
          margin-bottom: ${vw(9, 'mobile')};

          ${mq.greaterThan('tablet')} {
            ${font15(false)}
            text-align: justify;
            letter-spacing: unset;
            margin-bottom: ${vw(10, 'desktop')};
          }

          &:last-of-type {
            margin-bottom: 0;
          }
        }

      }

      > a {
        ${font12(true)}
        background-color: var(--color-text);
        border: 1px solid var(--color-text);
        color: var(--color-background);
        margin: 0 auto;
        max-width: ${vw(220, 'mobile')};
        padding: 15px ${vw(30, 'mobile')};
        transition: 300ms background-color linear, 300ms color linear;
        text-transform: uppercase;
        text-align: center;

        ${mq.greaterThan('tablet')} {
          ${font12(false)}
          padding: 15px ${vw(30, 'desktop')};
          margin: 0;
          max-width: ${vw(220, 'desktop')};
        }

        &:hover {
          border: 1px solid #000;
          background-color: #000;
          color: var(--color-background);
        }
      }
    }

    &:last-of-type {
      display: flex;
      flex-direction: column;
      gap: ${vw(40, 'mobile')};
      grid-column: 1 / span 6;
      grid-row: 2 / span 1;
      margin-top: ${vw(100, 'mobile')};
      padding: 0;

      ${mq.greaterThan('tablet')} {
        gap: ${vw(30, 'desktop')};
        grid-column: 7 / span 6;
        grid-row: 1 / span 2;
        margin-top:0;
        padding: 0 ${vw(50, 'desktop')};
      }

      .services {
        > ul {
          display: flex;
          flex-wrap: wrap;
          border-collapse: separate;

          >li {
            ${font10(true)}
            align-self: stretch;
            border-bottom: 1px solid rgba(60, 60, 59, 0.10);
            border-right: 1px solid rgba(60, 60, 59, 0.10);
            color: var(--color-text);
            display: flex;
            flex-direction: column;
            flex: 1 0 0;
            height: ${vw(100, 'mobile')};
            justify-content: center;
            margin: -1px 0 0 -1px; /* Offset the borders to prevent overlap */
            max-width: 33%;
            min-width: 33%;
            padding: 0 ${vw(5, 'mobile')};
            text-align: center;
            text-transform: uppercase;

            &:nth-child(-n+3) {
              border-top: none;
              margin-top: 0;
            }

            /* Remove border-bottom for the last row */
            &:nth-last-child(-n+3) {
              border-bottom: none;
            }

            /* Remove border-left for the first column of each row */
            &:nth-child(3n+1) {
              border-left: none;
              margin-left: 0;
            }

            /* Remove border-right for the last column of each row */
            &:nth-child(3n) {
              border-right: none;
            }

            &.empty {
              border: none !important;
              pointer-events: none;
              visibility: hidden;
            }

            &.middle_last {
              border-left: 1px solid rgba(60, 60, 59, 0.10);
              border-right: 1px solid rgba(60, 60, 59, 0.10);
              max-width: 33%;
            }

            ${mq.greaterThan('tablet')} {
              ${font10(false)}
              height: ${vw(100, 'desktop')};
              padding: 0 ${vw(20, 'desktop')};
            }

          }

        }

        > p,
        > h1,
        > h2,
        > h3,
        > h4,
        > h5,
        > h6,
        > span {
          ${font12(true)}
          opacity: 0.8;
          text-align: center;
          margin: ${vw(30, 'mobile')} ${vw(30, 'mobile')} 0;

          ${mq.greaterThan('tablet')} {
            ${font12(false)}
            margin: ${vw(30, 'desktop')} ${vw(30, 'desktop')} 0;
          }
        }
      }


      > button {
        ${font12(true)}
        ${createUnderline({ reverse: true })}
        margin: ${vw(10, 'mobile')} auto 0;

        /* ${mq.greaterThan('nexus7')} {
          margin: ${vw(10, 'tablet')} auto 0;
        } */

				&.only-desktop {
					${mq.lessThan('tablet')} {
						display: none;
					}
				}

        ${mq.greaterThan('tablet')} {
          ${font12(false)}
          margin: auto;
        }
      }
    }
  }

`

